import {
    fetchActivitiesCrmMetas,
    fetchDealsActivities,
    fetchDealsActivitiesInit,
    formatActivities,
    updateActivity,
    delActivity,
    updateActivitiesDashboardCardFilters,
    exportDealsActivities,
} from 'api/activities/activitiesService.js';
import { getIntervalWithShortcut } from 'Helpers/activities/activitiesDateFilters.js';
import { isEqual, isEmpty } from 'lodash';
import { TABS } from 'Dictionaries/activities/ActivitiesEnums.js';

let fetchActivitiesController = null;
const KEY_LOCALSTORAGE_ACTIVITIES_PAGE_KEY = 'activities-listing-page';
const KEY_LOCALSTORAGE_DASHBOARD_KEY = 'activities-listing-dashboard';
export default {
    namespaced: true,
    state() {
        return {
            activities: [],
            metas: null,
            loadingActivitiesPage: true,
            loadingTable: false,
            loadingMore: false,
            defaultFilter: {},
            filters: {},
            searchParams: {
                direction: 'asc',
                order: 'due_date',
                offset: '',
                limit: 25,
            },
            totalActivitiesInDB: 0,
            activityDataPanel: null,
            isDashboardCard: true,
            activitySelected: null,
            activityCardName: '',
            activeTab: TABS.LATE,
            pagination: null,
            listingname: 'activities-deals',
            hasFullExport: false,
            display: { export: false, exportType: 'csv' },
        };
    },
    getters: {
        getMetas: (state) => state.metas,
        getDisplay: (state) => state.display,
        meta: (state, getters, rootState) => ({
            filters: state.filters,
            columns: rootState.listingSettingsStores?.columns
                ?.filter((col) => col.isVisible)
                ?.map((col) => col.code) ?? [],
        }),
        getActivities: (state) => state.activities,
        getLoadingActivitiesPage: (state) => state.loadingActivitiesPage,
        getLoadingTable: (state) => state.loadingTable,
        getDefaultFilters: (state) => state.defaultFilter,
        getFilters: (state) => state.filters,
        getSearchParams: (state) => ({
            ...state.searchParams,
            embed: ['acl'],
        }),
        getTotalActivitiesInDB: (state) => state.totalActivitiesInDB,
        getLoadingMore: (state) => state.loadingMore,
        getActivityDataPanel: (state) => state.activityDataPanel,
        getActivitySelected: (state) => state.activitySelected,
        getIsDashboardCard: (state) => state.isDashboardCard,
        getLocalStorageNameKey: (state) => {
            return state.isDashboardCard
                ? KEY_LOCALSTORAGE_DASHBOARD_KEY
                : KEY_LOCALSTORAGE_ACTIVITIES_PAGE_KEY;
        },
        getActiveTab: (state) => state.activeTab,
        getPagination: (state) => state.pagination,
    },
    mutations: {
        setActivities: (state, payload) => (state.activities = payload),
        setMetas: (state, payload) => (state.metas = payload),
        setLoadingActivitiesPage: (state, payload) => (state.loadingActivitiesPage = payload),
        setLoadingTable: (state, payload) => (state.loadingTable = payload),
        setDefaultFilters: (state, payload) => (state.defaultFilter = payload),
        setFilters: (state, payload) => {
            state.filters = payload;
        },
        setSearchParams: (state, payload) => (state.searchParams = payload),
        setTotalActivitiesInDB: (state, payload) => (state.totalActivitiesInDB = payload),
        setLoadingMore: (state, payload) => (state.loadingMore = payload),
        setActivityDataPanel: (state, payload) => (state.activityDataPanel = payload),
        setActivitySelected: (state, payload) => (state.activitySelected = payload),
        setIsDashboardCard: (state, payload) => (state.isDashboardCard = payload),
        setActivityCardName: (state, payload) => (state.activityCardName = payload),
        setActiveTab: (state, tab) => (state.activeTab = tab),
        setPagination: (state, pagination) => (state.pagination = pagination),
        toggleDisplay: (state, display) => (state.display[display] = !state.display[display]),
        hideDisplay: (state, display) => (state.display[display] = false),
        updateExportType: (state, type) => (state.display.exportType = type),
    },
    actions: {
        async initActivityStore({ commit, rootState, getters, dispatch }, isDashboardCard = false) {
            const defaultFilters = {
                staffs: [rootState.user.id],
                completed: false,
                entities: ['task', 'phonecall'],
            };
            await commit('setIsDashboardCard', isDashboardCard);
            const nameKey = isDashboardCard
                ? KEY_LOCALSTORAGE_DASHBOARD_KEY
                : KEY_LOCALSTORAGE_ACTIVITIES_PAGE_KEY;
            const sortStorage = JSON.parse(
                localStorage.getItem(`${nameKey}:sort_${rootState.user.id}`),
            );

            let searchParams = {
                ...getters.getSearchParams,
                limit: isDashboardCard ? 25 : rootState.listingSettingsStores.pagination.limit,
            };
            if (sortStorage) {
                searchParams = {
                    ...searchParams,
                    direction: sortStorage.direction,
                    order: sortStorage.order,
                };
            }
            await commit('setSearchParams', searchParams);

            let filters = { ...defaultFilters, ...getters.getFilters };
            if (!isDashboardCard) {
                const persistedSelectedTab =
                    localStorage.getItem(`activities-listing:tab-selected_${rootState.user.id}`) ??
                    TABS.LATE;
                filters.completed = persistedSelectedTab === TABS.DONE;
                commit('setActiveTab', persistedSelectedTab);
                if (persistedSelectedTab === TABS.LATE) {
                    filters = { ...filters, due_date: { end: moment().toISOString() } };
                }

                const persistedFilters = JSON.parse(
                    localStorage.getItem(`activities-listing:filters_${rootState.user.id}`),
                );
                if (persistedFilters) {
                    for (const filter of persistedFilters) {
                        filters[filter.code] = filter.value;

                        if (filter.code !== 'due_date') {
                            continue;
                        }

                        if (!filter.optionsSelected?.length) {
                            delete filters['due_date'];
                            continue;
                        }

                        if (filter.optionsSelected === 'customRange') {
                            filters['due_date'] = {
                                start: filter.value.start,
                                end: filter.value.end,
                            };
                            continue;
                        }

                        const { startDate, endDate } = getIntervalWithShortcut(
                            filter.optionsSelected,
                        );

                        if (startDate === '' || endDate === '') {
                            delete filters['due_date'];
                            continue;
                        }

                        filters['due_date'] = {
                            start: moment(startDate).toISOString(),
                            end: moment(endDate).endOf('day').toISOString(),
                        };
                    }
                }
            }
            commit('setDefaultFilters', defaultFilters);
            commit('setFilters', filters);
            commit('setLoadingActivitiesPage', true);

            const callFetchMetas = fetchActivitiesCrmMetas();
            const callFetchActivities = fetchDealsActivitiesInit({
                params: getters.getSearchParams,
                filters: filters,
            });
            Promise.allSettled([callFetchMetas, callFetchActivities])
                .then((res) => {
                    let metas = null;
                    if (res[0].status === 'fulfilled') {
                        metas = res[0].value.data;
                        commit('setMetas', metas);
                    } else {
                        console.error(res[0].reason);
                    }

                    if (res[1].status === 'fulfilled' && !!metas) {
                        const activities = formatActivities(res[1].value.data, metas);
                        commit('setActivities', activities);
                        const pagination = res[1].value.data.pagination;
                        if (!getters.getIsDashboardCard) {
                            dispatch('listingSettingsStores/updatePagination', pagination, {
                                root: true,
                            });
                        }
                        commit('setSearchParams', {
                            ...getters.getSearchParams,
                            ...pagination,
                        });
                        commit('setTotalActivitiesInDB', pagination.total);
                    } else if (res[1].status === 'rejected') {
                        console.error(res[1].reason);
                    }
                })
                .finally(() => {
                    commit('setLoadingActivitiesPage', false);
                });
        },
        async fetchActivitiesCrm({ commit, getters, dispatch }) {
            let abortedRequest = false;
            try {
                if (fetchActivitiesController) {
                    fetchActivitiesController.abort();
                }
                fetchActivitiesController = new AbortController();
                if (getters.getActivities.length === 0) {
                    commit('setLoadingTable', true);
                }
                const { activities, pagination } = await fetchDealsActivities(
                    getters.getMetas,
                    getters.getSearchParams,
                    getters.getFilters,
                    fetchActivitiesController.signal,
                );
                commit('setActivities', getters.getActivities.concat(activities));
                commit('setSearchParams', {
                    ...getters.getSearchParams,
                    offset: pagination.offset,
                });
                commit('setTotalActivitiesInDB', pagination.total);
                if (!getters.getIsDashboardCard) {
                    dispatch('listingSettingsStores/updatePagination', pagination, {
                        root: true,
                    });
                }

                fetchActivitiesController = null;
            } catch (error) {
                abortedRequest = error.name === 'AbortError';
                if (!abortedRequest) {
                    console.error(error);
                }
            } finally {
                if (!abortedRequest) {
                    commit('setLoadingMore', false);
                    commit('setLoadingTable', false);
                }
            }
        },
        async refreshActivities({ dispatch }) {
            await dispatch('clearActivities');
            await dispatch('updateSearchParams', { offset: '' });
            dispatch('updateActivitySelected', null);
            dispatch('fetchActivitiesCrm');
        },
        clearActivities({ commit }) {
            commit('setTotalActivitiesInDB', 0);
            commit('setActivities', []);
        },
        async updateFilters({ commit, getters, state }, filters) {
            if (
                getters.getIsDashboardCard &&
                !isEmpty(getters.getFilters) &&
                !isEmpty(filters) &&
                !isEqual(filters, getters.getFilters)
            ) {
                await updateActivitiesDashboardCardFilters(filters, state.activityCardName);
            }
            commit('setFilters', filters);
        },
        async updateSearchParams({ commit, getters, rootState }, payload) {
            if (!!payload.order && !!payload.direction) {
                const nameKey = getters.getIsDashboardCard
                    ? KEY_LOCALSTORAGE_DASHBOARD_KEY
                    : KEY_LOCALSTORAGE_ACTIVITIES_PAGE_KEY;
                localStorage.setItem(
                    `${nameKey}:sort_${rootState.user.id}`,
                    JSON.stringify({
                        direction: payload.direction,
                        order: payload.order,
                    }),
                );
            }
            await commit('setSearchParams', { ...getters.getSearchParams, ...payload });
        },
        updateLoadingMore({ commit }, loadingMore) {
            commit('setLoadingMore', loadingMore);
        },
        updateLoadingTable({ commit }, loadingTable) {
            commit('setLoadingTable', loadingTable);
        },
        updateActivityDataPanel({ commit }, activityDataPanel) {
            commit('setActivityDataPanel', activityDataPanel);
        },
        updateMetasData({ commit }, metas) {
            commit('setMetas', metas);
        },
        updateActivityCardName({ commit }, activityCardName) {
            commit('setActivityCardName', activityCardName);
        },
        updateActivityDueDate({ commit }, { activity, rescheduledDate }) {
            const dueDate = activity.due_date ?? activity.date;
            const id = activity.activityId ?? activity.id;

            const today = moment(rescheduledDate);
            // // Keep due date time and transpose to today before incrementing so it will increment relatively to today's date at the original time
            const nextDate = moment(dueDate).set({
                year: today.year(),
                month: today.month(),
                date: today.date(),
            });
            commit('setLoadingTable', true);

            return updateActivity(
                activity.type,
                id,
                activity.type === 'task'
                    ? { due_date: nextDate.toISOString(true) }
                    : { date: nextDate.toISOString(true) },
            );
        },
        deleteActivity({}, activity) {
            const id = activity.activityId ?? activity.id;
            return delActivity(activity.type, id);
        },
        async runExport({ state, getters }, data) {
            const columns = data.allColumns ? null : getters.meta.columns;
            return exportDealsActivities(state.searchParams, {
                output_format: data.outputFormat,
                charset: data.charset ?? 'UTF-8',
                columns,
                filters: state.filters,
            });
        },
        updateActivitySelected({ commit }, activity) {
            if (!activity) {
                commit('setActivitySelected', null);
                return;
            }
            commit('setActivitySelected', { id: activity.activityId, type: activity.type });
        },
        updateActiveTab({ commit, rootState }, tab) {
            commit('setActiveTab', tab);
            localStorage.setItem(`activities-listing:tab-selected_${rootState.user.id}`, tab);
        },
    },
};
